<script lang="ts" setup>
import { computed, h, ref, watch, onBeforeUnmount } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useQuery } from "@tanstack/vue-query";
import {
    NDataTable,
    NSpin,
    NDropdown,
    type DataTableColumns,
    useMessage,
    NPopover,
} from "naive-ui";
import { patchProperty, searchPropertiesV2 } from "../../services/contador/properties.service";
import type { PropertiesSearchRequest, PropertiesWhere, WhereAndOr } from "../../models/search";
import type { Property, Comment } from "../../models/contador";
import { usePortalBreakpoints } from "../../hooks/usePortalBreakpoints";
import { queryRef } from "../../router/helpers.ts";
import NotesDrawer from "../../components/NotesDrawer.vue";
import ProperText from "../../components/proper-inputs/proper-text.vue";
import ProperSwitch from "../../components/proper-inputs/proper-switch.vue";
import ProperSelect from "../../components/proper-inputs/proper-select.vue";
import { debounce } from "lodash";
import ConfirmationModal from "../../components/ConfirmationModal.vue";
import { renderNotesCell } from "../../utils/notesColumn";

const { isMobile } = usePortalBreakpoints();
const message = useMessage();
const route = useRoute();
const router = useRouter();

// Drawer states
const showDrawer = ref(false);
const openedProperty = ref<Property | null>(null);
const openedPropertyId = queryRef("property-notes-and-activity", "");
const openedDrawerTab = queryRef("property-drawer-tab", "notes");
const resolvingFromUrl = ref(0);
const conditionTimeoutMs = 3000;

function stripAccents(input: string): string {
    if (!input) return "";
    try {
        return input.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    } catch {
        return input;
    }
}

// Search state
const searchParamKey = "properties-search";
const properScopeParamKey = "properties-proper-scope";
const groupsParamKey = "properties-groups";
const typesParamKey = "properties-types";

function normalizeStringParam(value: unknown): string {
    if (Array.isArray(value)) {
        return value[0] ?? "";
    }
    return typeof value === "string" ? value : "";
}

function normalizeArrayParam(value: unknown): string[] {
    if (!value) return [];
    if (Array.isArray(value)) {
        return value.filter((item): item is string => typeof item === "string" && item.length > 0);
    }
    return typeof value === "string" && value.length > 0 ? [value] : [];
}

function arraysEqual(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false;
    return a.every((val, idx) => val === b[idx]);
}

function setQueryParam(paramName: string, value: string | string[] | null) {
    const nextQuery = { ...route.query };
    const normalizedValue = Array.isArray(value) ? value : (value ?? "");
    if (Array.isArray(normalizedValue) && normalizedValue.length === 0) {
        delete nextQuery[paramName];
    } else if (!Array.isArray(normalizedValue) && normalizedValue === "") {
        delete nextQuery[paramName];
    } else {
        nextQuery[paramName] = normalizedValue as any;
    }
    router.replace({ query: nextQuery });
}

const searchTerm = ref<string>(normalizeStringParam(route.query[searchParamKey]));
const debouncedSearch = ref<string>("");
const applySearch = debounce((val: string) => {
    debouncedSearch.value = stripAccents((val || "").trim());
}, 300);
watch(
    () => searchTerm.value,
    (val) => {
        applySearch(val);
    },
    { immediate: true },
);

// Filter state
const filterProperScope = ref<string | null>(
    normalizeStringParam(route.query[properScopeParamKey]) || null,
);
const filterGroups = ref<string[]>(normalizeArrayParam(route.query[groupsParamKey]));
const filterTypes = ref<string[]>(normalizeArrayParam(route.query[typesParamKey]));

watch(
    () => route.query[searchParamKey],
    (value) => {
        const next = normalizeStringParam(value);
        if (next !== searchTerm.value) {
            searchTerm.value = next;
        }
    },
);

watch(
    () => route.query[properScopeParamKey],
    (value) => {
        const next = normalizeStringParam(value) || null;
        if (next !== filterProperScope.value) {
            filterProperScope.value = next;
        }
    },
);

watch(
    () => route.query[groupsParamKey],
    (value) => {
        const next = normalizeArrayParam(value);
        if (!arraysEqual(next, filterGroups.value)) {
            filterGroups.value = next;
        }
    },
);

watch(
    () => route.query[typesParamKey],
    (value) => {
        const next = normalizeArrayParam(value);
        if (!arraysEqual(next, filterTypes.value)) {
            filterTypes.value = next;
        }
    },
);

watch(
    () => searchTerm.value,
    (value) => {
        const current = normalizeStringParam(route.query[searchParamKey]);
        if (value !== current) {
            setQueryParam(searchParamKey, value);
        }
    },
);

watch(
    () => filterProperScope.value,
    (value) => {
        const current = normalizeStringParam(route.query[properScopeParamKey]) || null;
        if (value !== current) {
            setQueryParam(properScopeParamKey, value);
        }
    },
);

watch(
    () => filterGroups.value,
    (value) => {
        const current = normalizeArrayParam(route.query[groupsParamKey]);
        if (!arraysEqual(value, current)) {
            setQueryParam(groupsParamKey, value);
        }
    },
    { deep: true },
);

watch(
    () => filterTypes.value,
    (value) => {
        const current = normalizeArrayParam(route.query[typesParamKey]);
        if (!arraysEqual(value, current)) {
            setQueryParam(typesParamKey, value);
        }
    },
    { deep: true },
);

// Active filters helpers
const activeFiltersCount = computed(() => {
    let count = 0;
    if (filterProperScope.value !== null) count += 1;
    if (filterGroups.value.length > 0) count += 1;
    if (filterTypes.value.length > 0) count += 1;
    return count;
});
const hasActiveFilters = computed(() => activeFiltersCount.value > 0);

const properScopeOptions = [
    { label: "In Proper Scope", value: "true" },
    { label: "Out of Scope", value: "false" },
];
const groupOptions = [
    { label: "Individual", value: "individual" },
    { label: "Group", value: "group" },
];
const propertyTypeOptions = [
    { label: "Affordable housing", value: "Affordable housing" },
    { label: "Apartment", value: "Apartment" },
    { label: "Association", value: "Association" },
    { label: "commercial", value: "commercial" },
    { label: "Commercial", value: "Commercial" },
    { label: "Commercial, Storage", value: "Commercial, Storage" },
    { label: "Condo", value: "Condo" },
    { label: "Condominium", value: "Condominium" },
    { label: "Condo/Townhome", value: "Condo/Townhome" },
    { label: "Corp Books", value: "Corp Books" },
    { label: "corporate", value: "corporate" },
    { label: "Corporate", value: "Corporate" },
    { label: "Development Fund", value: "Development Fund" },
    { label: "Duplex", value: "Duplex" },
    { label: "HOA", value: "HOA" },
    { label: "Income Fund", value: "Income Fund" },
    { label: "Investment entity", value: "Investment entity" },
    { label: "Manufactured homes", value: "Manufactured homes" },
    { label: "Manufactured-Housing", value: "Manufactured-Housing" },
    { label: "Mixed-Use", value: "Mixed-Use" },
    { label: "Mobile Homes", value: "Mobile Homes" },
    { label: "Multifamily", value: "Multifamily" },
    { label: "Multi-Family", value: "Multi-Family" },
    { label: "Multiplex", value: "Multiplex" },
    { label: "No Fund", value: "No Fund" },
    { label: "No Information", value: "No Information" },
    { label: "Other", value: "Other" },
    { label: "PO Box", value: "PO Box" },
    { label: "Rental", value: "Rental" },
    { label: "residential", value: "residential" },
    { label: "Residential", value: "Residential" },
    { label: "Residential, Multi-Family", value: "Residential, Multi-Family" },
    { label: "Self Storage", value: "Self Storage" },
    { label: "Self-Storage", value: "Self-Storage" },
    { label: "Senior Housing", value: "Senior Housing" },
    { label: "Single-Family", value: "Single-Family" },
    { label: "Single Family Home", value: "Single Family Home" },
    { label: "Student-Housing", value: "Student-Housing" },
    { label: "Townhouse", value: "Townhouse" },
    { label: "Vacation", value: "Vacation" },
];

const properScopeFilterBool = computed<boolean | null>(() =>
    filterProperScope.value === "true" ? true : filterProperScope.value === "false" ? false : null,
);

function clearFilters() {
    filterProperScope.value = null;
    filterGroups.value = [];
    filterTypes.value = [];
}

// Pagination and data states
const pageLimit = 100;
const properties = ref<Property[]>([]);
const total = ref<number | undefined>();
const nextOffset = ref(0);
const lastPageCount = ref(0);
const isFetchingMore = ref(false);

const buildV2SearchPayload = (opts: {
    offset: number;
    limit: number;
    includeComments?: boolean;
}) => {
    const where: WhereAndOr<PropertiesWhere> = { AND: [] };

    if (debouncedSearch.value) {
        where.AND!.push({
            OR: [
                { name: { contains: debouncedSearch.value } },
                { short_name: { contains: debouncedSearch.value } },
                { owners: { contains: debouncedSearch.value } },
                { address: { contains: debouncedSearch.value } },
            ],
        } as WhereAndOr<PropertiesWhere>);
    }

    if (properScopeFilterBool.value !== null) {
        where.AND!.push({ properScope: { equals: !!properScopeFilterBool.value } });
    }

    if (filterGroups.value.length === 1) {
        where.AND!.push({ individualGroup: { equals: filterGroups.value[0] } });
    } else if (filterGroups.value.length > 1) {
        where.AND!.push({
            OR: filterGroups.value.map(
                (g) => ({ individualGroup: { equals: g } }) as PropertiesWhere,
            ),
        } as WhereAndOr<PropertiesWhere>);
    }

    if (filterTypes.value.length > 0) {
        where.AND!.push({ type: { in: filterTypes.value } });
    }

    if (opts.includeComments) {
        where.AND!.push({ include_comments: { equals: true } });
    }

    const payload: PropertiesSearchRequest = {
        where,
        limit: opts.limit,
        offset: opts.offset,
    };

    return payload;
};

const { data: propertiesResponse, isLoading: loadingProperties } = useQuery({
    queryKey: computed(() => [
        "properties-v2",
        pageLimit,
        debouncedSearch.value,
        properScopeFilterBool.value,
        JSON.stringify(filterGroups.value),
        JSON.stringify(filterTypes.value),
    ]),
    queryFn: async () => {
        const payload = buildV2SearchPayload({
            offset: 0,
            limit: pageLimit,
            includeComments: true,
        });
        return await searchPropertiesV2(payload);
    },
});

/**
 * Drawer related functions
 */
async function waitForCondition(
    check: () => boolean,
    timeoutMs = conditionTimeoutMs,
    intervalMs = 50,
) {
    if (check()) return true;
    return await new Promise<boolean>((resolve) => {
        const start = Date.now();
        const timer = setInterval(() => {
            if (check()) {
                clearInterval(timer);
                resolve(true);
            } else if (Date.now() - start > timeoutMs) {
                clearInterval(timer);
                resolve(false);
            }
        }, intervalMs);
    });
}

/**
 * if "property-notes-and-activity" is present in the URL, try to render the info in the drawer coming from
 * "propertiesResponse", or fetch it directly if not found, not loaded or
 * the first page does not include the property using "getPropertiesByIds".
 *
 */
async function openDrawerForPropertyId(propId: string) {
    const token = ++resolvingFromUrl.value;

    // Wait for the first page query to finish (if it runs)
    await waitForCondition(() => !loadingProperties.value, 12000);
    if (token !== resolvingFromUrl.value) return;

    // Try to find the property in the loaded first page
    let property = properties.value.find((p) => p.id === propId) || null;

    // If not found, fetch it directly by id as fallback
    if (!property) {
        try {
            const payload: PropertiesSearchRequest = {
                where: {
                    AND: [{ id: { equals: propId } }, { include_comments: { equals: true } }],
                } as WhereAndOr<PropertiesWhere>,
                limit: 1,
                offset: 0,
            };
            const resp = await searchPropertiesV2(payload);
            const items = resp?.items ?? [];
            if (token !== resolvingFromUrl.value) return;
            if (items && items.length > 0) {
                property = items[0] ?? null;
                // Optionally merge into current list if not present
                if (property && !properties.value.some((p) => p.id === property!.id)) {
                    properties.value = [property, ...properties.value];
                }
            }
        } catch (e) {
            // ignore; handled below
        }
    }

    if (!property) {
        message.error("Property not found in current list");
        openedPropertyId.value = "";
        return;
    }

    openedProperty.value = property;
    showDrawer.value = true;
}

watch(
    () => openedPropertyId.value,
    (propId) => {
        if (!propId) {
            return;
        }
        void openDrawerForPropertyId(propId);
    },
    { immediate: true },
);

watch(
    () => showDrawer.value,
    (isOpen) => {
        if (!isOpen) {
            openedPropertyId.value = "";
        }
    },
);

onBeforeUnmount(() => {
    openedPropertyId.value = "";
    applySearch.cancel();
});

const dropdownOptions = [
    { label: "Deactivate Proper", key: "deactivate", disabled: true },
    { label: "Request Scope Removal", key: "request-scope", disabled: true },
    { label: "View Notes", key: "view-notes" },
];

function handleDropdownSelect(key: string | number, row: Property) {
    if (key === "deactivate") {
    } else if (key === "request-scope") {
    } else if (key === "view-notes") {
        const property = properties.value.find((p) => p.id === row.id) || null;
        if (!property) {
            message.error("Property not found");
            return;
        }
        openedProperty.value = property;
        // store the property id in the URL
        openedPropertyId.value = property.id;
        showDrawer.value = true;
    }
}

// Event handlers for PropertyNotesDrawer
function handleCommentCreated(comment: Comment) {
    // Append to openedProperty and properties list
    if (openedProperty.value) {
        const existing = openedProperty.value.comments || [];
        openedProperty.value = {
            ...openedProperty.value,
            comments: [{ ...comment }, ...existing],
        } as Property;
    }
    const idx = properties.value.findIndex((p) => p.id === openedProperty.value?.id);
    if (idx >= 0) {
        const target = properties.value[idx];
        const existing = target.comments || [];
        properties.value[idx] = {
            ...target,
            comments: [{ ...comment }, ...existing],
        } as Property;
    }
}

function handleCommentUpdated(updated: Comment) {
    // Update openedProperty and properties lists with the updated comment
    if (openedProperty.value) {
        const list = openedProperty.value.comments || [];
        const idx = list.findIndex((c) => c.id === updated.id);
        if (idx >= 0) {
            const newList = [...list];
            newList[idx] = { ...newList[idx], ...updated } as Comment;
            openedProperty.value = {
                ...openedProperty.value,
                comments: newList,
            } as Property;
        }
    }
    const propIdx = properties.value.findIndex((p) => p.id === openedProperty.value?.id);
    if (propIdx >= 0) {
        const target = properties.value[propIdx];
        const list = target.comments || [];
        const idx = list.findIndex((c) => c.id === updated.id);
        if (idx >= 0) {
            const newList = [...list];
            newList[idx] = { ...newList[idx], ...updated } as Comment;
            properties.value[propIdx] = { ...target, comments: newList } as Property;
        }
    }
}

function handleCommentDeleted(commentId: string) {
    // Remove from openedProperty comments
    if (openedProperty.value) {
        const filtered = (openedProperty.value.comments || []).filter((c) => c.id !== commentId);
        openedProperty.value = {
            ...openedProperty.value,
            comments: filtered,
        } as Property;
    }
    // Also update in properties list for this property
    const idx = properties.value.findIndex((p) => p.id === openedProperty.value?.id);
    if (idx >= 0) {
        const target = properties.value[idx];
        const filtered = (target.comments || []).filter((c) => c.id !== commentId);
        properties.value[idx] = {
            ...target,
            comments: filtered,
        } as Property;
    }
}

watch(
    propertiesResponse,
    (response) => {
        if (!response) {
            properties.value = [];
            total.value = 0;
            nextOffset.value = 0;
            lastPageCount.value = 0;
            return;
        }

        const items = response.items ?? [];
        properties.value = items;
        total.value = response.total ?? items.length;
        nextOffset.value = ((response.offset ?? 0) as number) + items.length;
        lastPageCount.value = items.length;
    },
    { immediate: true },
);

const canLoadMore = computed(() => {
    if (total.value !== undefined && total.value !== null) {
        return properties.value.length < total.value;
    }

    return lastPageCount.value === pageLimit;
});

const loadMoreProperties = async () => {
    if (isFetchingMore.value || loadingProperties.value || !canLoadMore.value) {
        return;
    }

    isFetchingMore.value = true;
    try {
        const payload = buildV2SearchPayload({ offset: nextOffset.value, limit: pageLimit });
        const response = await searchPropertiesV2(payload);

        if (!response) {
            lastPageCount.value = 0;
            return;
        }

        const newItems = response.items ?? [];
        if (newItems.length > 0) {
            properties.value = [...properties.value, ...newItems];
            nextOffset.value += newItems.length;
        }
        lastPageCount.value = newItems.length;
        if (response.total !== undefined) {
            total.value = response.total as number;
        }
    } finally {
        isFetchingMore.value = false;
    }
};

const handleScroll = (event: Event) => {
    const target = event.target as HTMLElement;
    const remaining = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (remaining < 200) {
        void loadMoreProperties();
    }
};

function usePropertyPatch() {
    const updatingProperScope = ref<Record<string, boolean>>({});

    function setUpdating(id: string, val: boolean) {
        updatingProperScope.value = { ...updatingProperScope.value, [id]: val };
    }

    function isUpdating(id: string): boolean {
        return Boolean(updatingProperScope.value[id]);
    }

    function mergePropertyLocally(id: string, patch: Partial<Property>) {
        const idx = properties.value.findIndex((p) => p.id === id);
        if (idx >= 0) {
            properties.value[idx] = { ...properties.value[idx], ...patch } as Property;
        }
        if (openedProperty.value?.id === id) {
            openedProperty.value = { ...openedProperty.value, ...patch } as Property;
        }
    }

    async function toggleProperScope(row: Property, value: boolean) {
        if (isUpdating(row.id)) return;
        const original = row.properScope;

        setUpdating(row.id, true);

        try {
            const updated = await patchProperty(row.id, { properScope: value });
            if (updated) {
                mergePropertyLocally(row.id, updated as Partial<Property>);
            } else {
                mergePropertyLocally(row.id, { properScope: value });
            }

            await refetchPages();

            if (value) {
                message.success("In Proper Scope");
            } else {
                message.warning("Out of Scope");
            }
        } catch (e) {
            // Revert on error (only needed if we optimistically updated, but we don't anymore)
            mergePropertyLocally(row.id, { properScope: original });
            message.error("Failed to update Proper Scope");
        } finally {
            setUpdating(row.id, false);
        }
    }

    // State for confirmation modal before toggling Proper Scope
    const showProperScopeConfirm = ref(false);
    const pendingProperScopeRow = ref<Property | null>(null);
    const pendingProperScopeValue = ref<boolean | null>(null);

    function requestToggleProperScope(row: Property, value: boolean) {
        if (isUpdating(row.id)) return;
        pendingProperScopeRow.value = row;
        pendingProperScopeValue.value = value;
        showProperScopeConfirm.value = true;
    }

    function confirmToggleProperScope() {
        const row = pendingProperScopeRow.value;
        const val = pendingProperScopeValue.value;
        if (!row || val === null) {
            showProperScopeConfirm.value = false;
            pendingProperScopeRow.value = null;
            pendingProperScopeValue.value = null;
            return;
        }
        toggleProperScope(row, val as boolean);
        showProperScopeConfirm.value = false;
        pendingProperScopeRow.value = null;
        pendingProperScopeValue.value = null;
    }

    function cancelToggleProperScope() {
        showProperScopeConfirm.value = false;
        pendingProperScopeRow.value = null;
        pendingProperScopeValue.value = null;
    }

    return {
        isUpdating,
        showProperScopeConfirm,
        pendingProperScopeValue,
        requestToggleProperScope,
        confirmToggleProperScope,
        cancelToggleProperScope,
    };
}
const {
    isUpdating,
    showProperScopeConfirm,
    pendingProperScopeValue,
    requestToggleProperScope,
    confirmToggleProperScope,
    cancelToggleProperScope,
} = usePropertyPatch();

const columns = computed<DataTableColumns<Property>>(() => [
    {
        title: "NAME",
        key: "name",
        width: isMobile.value ? 220 : "22%",
        minWidth: isMobile.value ? 220 : 200,
        render: (row) => h("span", { class: "font-semibold" }, row.name),
    },
    {
        title: "OWNER",
        key: "owners",
        width: isMobile.value ? 260 : "18%",
        minWidth: isMobile.value ? 220 : 160,
        render: (row) => row.owners || "-",
    },
    {
        title: "ADDRESS",
        key: "address",
        width: isMobile.value ? 320 : "24%",
        minWidth: isMobile.value ? 280 : 240,
        render: (row) => row.address || "-",
    },
    {
        title: "PROPERTY GROUP",
        key: "individualGroup",
        width: isMobile.value ? 160 : 160,
        minWidth: 140,
        render: (row) =>
            row.individualGroup
                ? h("span", { class: "capitalize" }, String(row.individualGroup))
                : "-",
    },
    {
        title: "PROPERTY TYPE",
        key: "type",
        width: isMobile.value ? 160 : 160,
        minWidth: 140,
        render: (row) => (row.type ? String(row.type) : "-"),
    },
    {
        title: "UNITS",
        key: "unitCount",
        width: isMobile.value ? 100 : 100,
        minWidth: 90,
        align: "center",
        render: (row) => (typeof row.unitCount === "number" ? row.unitCount : "-"),
    },
    {
        title: "NOTES",
        key: "comments",
        width: isMobile.value ? 260 : "22%",
        minWidth: isMobile.value ? 220 : 260,
        render: (row) =>
            renderNotesCell({
                row,
                onOpenNotes: openNotesForRow,
            }),
    },
    {
        title: "PROPER SCOPE ",
        key: "properScope",
        width: 125,
        align: "center",
        render: (row) =>
            h(ProperSwitch as any, {
                modelValue: !!row.properScope,
                disabled: isUpdating(row.id),
                loading: isUpdating(row.id),
                "onUpdate:modelValue": (val: boolean) => requestToggleProperScope(row, val),
            }),
    },
    {
        title: "",
        key: "actions",
        width: 60,
        align: "center",
        render: (row) =>
            h(
                NDropdown,
                {
                    options: dropdownOptions,
                    trigger: "click",
                    onSelect: (key: string | number) => handleDropdownSelect(key, row),
                },
                {
                    default: () =>
                        h(
                            "button",
                            {
                                class: "flex items-center justify-center w-full h-full cursor-pointer",
                            },
                            [h("span", { class: "material-symbols-outlined" }, "more_vert")],
                        ),
                },
            ),
    },
]);

const loading = computed(() => loadingProperties.value);

// Helper to replace items within the current properties array at a given offset
function replaceItemsAtOffset(offset: number, newItems: Property[]) {
    if (offset < 0) offset = 0;
    if (properties.value.length < offset) {
        properties.value = [...properties.value];
    }
    const before = properties.value.slice(0, offset);
    const after = properties.value.slice(offset + newItems.length);
    properties.value = [...before, ...newItems, ...after];
}

// Refetch current and previous page and merge results
async function refetchPages() {
    const currentOffset = Math.max(nextOffset.value - lastPageCount.value, 0);
    const prevOffset = Math.max(currentOffset - pageLimit, 0);

    const [currentResp, prevResp] = await Promise.all([
        searchPropertiesV2(
            buildV2SearchPayload({
                offset: currentOffset,
                limit: pageLimit,
                includeComments: true,
            }),
        ),
        currentOffset > 0
            ? searchPropertiesV2(
                  buildV2SearchPayload({
                      offset: prevOffset,
                      limit: pageLimit,
                      includeComments: true,
                  }),
              )
            : Promise.resolve(undefined),
    ]);

    if (currentResp?.items) {
        replaceItemsAtOffset(currentOffset, currentResp.items as Property[]);
        lastPageCount.value = currentResp.items.length;
        total.value = currentResp.total;
        nextOffset.value = Math.max(nextOffset.value, currentOffset + currentResp.items.length);
    }

    if (prevResp?.items && currentOffset > 0) {
        replaceItemsAtOffset(prevOffset, prevResp.items as Property[]);
        total.value = prevResp.total;
    }

    if (openedProperty.value) {
        const refreshed = properties.value.find((p) => p.id === openedProperty.value!.id);
        if (refreshed) {
            openedProperty.value = refreshed;
        }
    }
}

function openNotesForRow(row: Property) {
    const entity = properties.value.find((p) => p.id === row.id) || row;
    openedProperty.value = entity;
    openedPropertyId.value = entity.id;
    openedDrawerTab.value = "notes";
    showDrawer.value = true;
}
</script>

<template>
    <div class="overflow-x-auto min-w-0">
        <div class="flex items-center justify-between mb-3">
            <!-- Search bar -->
            <proper-text
                v-model="searchTerm"
                placeholder="Search by Name, Owner, or Address"
                class="flex-1 max-w-xl"
                :live-preview="false"
                :show-prefix="true"
                :clearable="true"
            />

            <!-- Filters -->
            <div class="flex items-center gap-2">
                <template v-if="hasActiveFilters">
                    <span class="text-xs text-neutral-600">{{ activeFiltersCount }} active</span>
                    <button class="text-xs underline active:scale-95" @click="clearFilters">
                        Clear
                    </button>
                </template>
                <n-popover style="width: 300px" trigger="click">
                    <template #trigger>
                        <button class="secondary-button">Filters</button>
                    </template>
                    <div>
                        <div class="mb-3">
                            <label class="text-xs font-medium text-neutral-600 block mb-1"
                                >Proper Scope</label
                            >
                            <proper-select
                                v-model="filterProperScope"
                                :options="properScopeOptions"
                                placeholder="Proper Scope"
                                :clearable="true"
                            />
                        </div>
                        <div class="mb-3">
                            <label class="text-xs font-medium text-neutral-600 block mb-1"
                                >Property Group</label
                            >
                            <proper-select
                                v-model="filterGroups"
                                :options="groupOptions"
                                placeholder="Property Group"
                                multiple
                                :clearable="true"
                            />
                        </div>
                        <div class="mb-1">
                            <label class="text-xs font-medium text-neutral-600 block mb-1"
                                >Property Type</label
                            >
                            <proper-select
                                v-model="filterTypes"
                                :options="propertyTypeOptions"
                                placeholder="Property Type"
                                multiple
                                :clearable="true"
                            />
                        </div>
                        <div class="flex justify-end mt-2">
                            <button
                                v-if="hasActiveFilters"
                                class="text-xs underline active:scale-95"
                                @click="clearFilters"
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                </n-popover>
            </div>
        </div>
        <n-data-table
            :columns="columns"
            :data="properties"
            :loading="loading || isFetchingMore"
            :bordered="true"
            :single-line="false"
            :single-column="false"
            :scroll-x="isMobile ? 1040 : undefined"
            size="small"
            :class="['rounded-lg']"
            :max-height="isMobile ? '60vh' : '80vh'"
            :on-scroll="handleScroll"
        >
            <template #loading>
                <div class="flex items-center justify-center mt-16 gap-2 bg-white p-3 rounded-lg">
                    <n-spin size="small" />
                    <p class="text-sm text-gray-500">Loading properties</p>
                </div>
            </template>
            <template #empty>
                <div class="py-6 text-sm text-gray-400">No properties found</div>
            </template>
        </n-data-table>
    </div>
    <notes-drawer
        v-model:show="showDrawer"
        v-model:tab="openedDrawerTab"
        :entity="openedProperty"
        type="property"
        :auto-focus-on-open="true"
        @comment-created="handleCommentCreated"
        @comment-updated="handleCommentUpdated"
        @comment-deleted="handleCommentDeleted"
        @refetch="refetchPages"
    />

    <confirmation-modal
        v-if="showProperScopeConfirm"
        :danger="pendingProperScopeValue === false"
        :title="
            pendingProperScopeValue === false
                ? 'Confirm: set Out of Scope'
                : 'Confirm: set In Proper Scope'
        "
        :content="
            pendingProperScopeValue === false
                ? 'Are you sure you want to set this property Out of Scope?'
                : 'Are you sure you want to set this property In Proper Scope?'
        "
        negative-text="Cancel"
        :positive-text="pendingProperScopeValue === false ? 'Set Out of Scope' : 'Set In Scope'"
        @positive-click="confirmToggleProperScope"
        @negative-click="cancelToggleProperScope"
    />
</template>
